import { css } from '@emotion/react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { theme } from '@frontend/theme';
import { Button, Text } from '@frontend/design-system';
import { createFileRoute } from '../utils/route-factory';

function AccessDeniedPage() {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as {
    redirect?: string;
    requiredAcl?: string;
    acls?: string;
  };

  const requiredAcl = search.requiredAcl;
  const acls = search.acls?.split(',').filter(Boolean) ?? [];
  const redirectUrl = search.redirect;

  return (
    <div
      css={css`
        display: flex;
        align-items: center;
        justify-content: center;
        min-height: 100vh;
        background-color: ${theme.colors.neutral5};
      `}
    >
      <main
        css={css`
          border: 1px solid ${theme.colors.neutral20};
          border-radius: ${theme.borderRadius.medium};
          background-color: ${theme.colors.white};
          padding: ${theme.spacing(5)};
          max-width: 520px;
          width: 100%;
          display: flex;
          flex-direction: column;
          gap: ${theme.spacing(3)};
        `}
      >
        <Text
          as='h1'
          css={css`
            font-size: ${theme.font.size.h1};
            font-weight: ${theme.font.weight.bold};
            color: ${theme.colors.neutral90};
          `}
        >
          Access Denied
        </Text>

        <Text
          css={css`
            color: ${theme.colors.neutral70};
            font-size: ${theme.font.size.medium};
          `}
        >
          You do not have the required permissions to access this page.
          {redirectUrl && ' Contact your administrator to request access.'}
        </Text>

        {requiredAcl && (
          <div
            css={css`
              background-color: ${theme.colors.neutral5};
              border: 1px solid ${theme.colors.neutral20};
              border-radius: ${theme.borderRadius.small};
              padding: ${theme.spacing(2)};
              display: flex;
              flex-direction: column;
              gap: ${theme.spacing(1)};
            `}
          >
            <Text
              weight='bold'
              css={css`
                font-size: ${theme.font.size.small};
                color: ${theme.colors.neutral70};
                text-transform: uppercase;
                letter-spacing: 0.5px;
              `}
            >
              Required Permission
            </Text>
            <Text
              css={css`
                font-family: monospace;
                font-size: ${theme.font.size.medium};
                color: ${theme.colors.critical50};
              `}
            >
              {requiredAcl}
            </Text>
          </div>
        )}

        {acls.length > 0 && (
          <div
            css={css`
              background-color: ${theme.colors.neutral5};
              border: 1px solid ${theme.colors.neutral20};
              border-radius: ${theme.borderRadius.small};
              padding: ${theme.spacing(2)};
              display: flex;
              flex-direction: column;
              gap: ${theme.spacing(1)};
            `}
          >
            <Text
              weight='bold'
              css={css`
                font-size: ${theme.font.size.small};
                color: ${theme.colors.neutral70};
                text-transform: uppercase;
                letter-spacing: 0.5px;
              `}
            >
              Route ACLs
            </Text>
            <ul
              css={css`
                margin: 0;
                padding: 0;
                list-style: none;
                display: flex;
                flex-direction: column;
                gap: ${theme.spacing(0.5)};
              `}
            >
              {acls.map((acl) => (
                <li key={acl}>
                  <Text
                    css={css`
                      font-family: monospace;
                      font-size: ${theme.font.size.medium};
                      color: ${theme.colors.neutral70};
                    `}
                  >
                    {acl}
                  </Text>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div
          css={css`
            display: flex;
            gap: ${theme.spacing(2)};
            margin-top: ${theme.spacing(1)};
          `}
        >
          <Button variant='secondary' onClick={() => navigate({ to: '/' } as any)}>
            Go Home
          </Button>
          {redirectUrl && (
            <Button variant='tertiary' onClick={() => navigate({ to: redirectUrl as any })}>
              Try Again
            </Button>
          )}
        </div>
      </main>
    </div>
  );
}

export const Route = createFileRoute({
  path: '/access-denied',
  component: AccessDeniedPage,
  type: 'auth',
  locationRequired: false,
});
